/*
 * Pestaña "Evaluar" de la ficha de tienda IGI. Muestra los criterios ACTIVOS agrupados por eje y grupo;
 * cada uno con nota de 1 a 5 (paso 0,1; acepta coma decimal) y comentario opcional. Criterio sin nota =
 * "sin dato" (no se envia). El guardado en el servidor sigue siendo SOLO con el boton Guardar, todo o
 * nada (create_evaluation/update_evaluacion no aceptan una evaluacion a medias) - lo que se autoguarda
 * es un BORRADOR local (localStorage, debounce de 1s), para no perder lo escrito si se cambia de
 * pestaña, se cierra el navegador o se pierde la conexion antes de tocar Guardar. Al montar, si hay un
 * borrador para esta tienda+evaluacion se recupera (avisando con un toast); se borra al guardar con
 * exito o al cancelar. Con cambios sin guardar avisa al salir: el Prompt de react-router vive en
 * tienda/index.js (una sola instancia para toda la ficha, via `onCambios`); el beforeunload queda aca,
 * independiente. Si el guardado en el servidor falla, el formulario conserva lo escrito (y el borrador
 * local sigue ahi) y muestra el mensaje del servidor.
 * Con `evaluacion` corrige esa evaluacion (PATCH: reemplaza las notas de criterios activos); sin ella
 * crea una nueva (POST). "Copiar notas de la evaluacion vigente" precarga las notas de `anterior`.
 * `criteriosCargando`/`criteriosError` llegan por props desde tienda/index.js (que es quien pide
 * GET /igi/criterios/): mientras los criterios cargan o si esa carga fallo, no se muestra el
 * formulario (evita el mensaje enganoso "Debe calificar al menos un criterio" cuando en realidad
 * los criterios todavia no llegaron).
 */
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import Button from "../../../../components/button";
import LoadingSpinner from "../../../../components/loading-spinner";
import {
    clear_igi,
    create_evaluacion,
    update_evaluacion,
    CREATE_EVALUACION_SUCCESS,
    UPDATE_EVALUACION_SUCCESS,
} from "../../../../redux/igi";
import {formatoFecha} from "../niveles";

const MENSAJE_SALIDA = "Tiene cambios sin guardar en la evaluación. ¿Desea salir sin guardar?";
const TEXTOS = [["observacion", "Observación"], ["accion", "Acción"], ["estrategia", "Estrategia"]];

const notasDesde = (evaluacion) => {
    const notas = {};
    if (evaluacion) {
        evaluacion.notas.forEach((n) => {
            notas[n.criterio] = {valor: String(n.valor), comentario: n.comentario || ""};
        });
    }
    return notas;
};

const textosDesde = (evaluacion) => ({
    observacion: evaluacion ? evaluacion.observacion : "",
    accion: evaluacion ? evaluacion.accion : "",
    estrategia: evaluacion ? evaluacion.estrategia : "",
});

const claveBorrador = (tiendaId, evaluacion) => `igi_evaluar_borrador_${tiendaId}_${evaluacion ? evaluacion.id : "nueva"}`;

const leerBorrador = (clave) => {
    try {
        const crudo = localStorage.getItem(clave);
        return crudo ? JSON.parse(crudo) : null;
    } catch (e) {
        return null;
    }
};

const escribirBorrador = (clave, datos) => {
    try {
        localStorage.setItem(clave, JSON.stringify(datos));
    } catch (e) {
        // localStorage no disponible (privado/lleno): el autoguardado es una ayuda, no una garantia.
    }
};

const borrarBorrador = (clave) => {
    try {
        localStorage.removeItem(clave);
    } catch (e) {
        // ver escribirBorrador
    }
};

// null = vacio (sin dato); NaN = invalido; numero con un decimal = valido
const leerNota = (texto) => {
    const limpio = String(texto).trim().replace(",", ".");
    if (limpio === "") {
        return null;
    }
    const valor = Number(limpio);
    if (!isFinite(valor) || valor < 1 || valor > 5 || Math.abs(valor * 10 - Math.round(valor * 10)) > 1e-9) {
        return NaN;
    }
    return Math.round(valor * 10) / 10;
};

const agrupar = (ejes) => ejes.map((eje) => {
    const grupos = [];
    eje.criterios.filter((c) => c.activo).forEach((c) => {
        let grupo = grupos.find((g) => g.nombre === c.grupo);
        if (!grupo) {
            grupo = {nombre: c.grupo, criterios: []};
            grupos.push(grupo);
        }
        grupo.criterios.push(c);
    });
    return {...eje, grupos};
});

const Evaluar = ({
    token,
    tiendaId,
    ejes,
    criteriosCargando,
    criteriosError,
    evaluacion,
    anterior,
    onGuardado,
    onCancelarCorreccion,
    onCambios,
}) => {
    const dispatch = useDispatch();
    const _create = useSelector((state) => state.igi_module._create_evaluacion);
    const _update = useSelector((state) => state.igi_module._update_evaluacion);
    const clave = claveBorrador(tiendaId, evaluacion);
    const borrador = leerBorrador(clave);
    const [notas, setNotas] = useState(() => (borrador ? borrador.notas : notasDesde(evaluacion)));
    const [textos, setTextos] = useState(() => (borrador ? borrador.textos : textosDesde(evaluacion)));
    const [conCambios, setConCambios] = useState(() => Boolean(borrador));
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (borrador) {
            toast.info("Se recuperó un borrador sin guardar de esta evaluación");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!conCambios) {
            return undefined;
        }
        const temporizador = setTimeout(() => escribirBorrador(clave, {notas, textos}), 1000);
        return () => clearTimeout(temporizador);
    }, [notas, textos, conCambios, clave]);

    const ejesAgrupados = agrupar(ejes);
    const activos = [];
    ejesAgrupados.forEach((eje) => eje.grupos.forEach((g) => g.criterios.forEach((c) => activos.push(c))));

    useEffect(() => {
        dispatch(clear_igi(CREATE_EVALUACION_SUCCESS));
        dispatch(clear_igi(UPDATE_EVALUACION_SUCCESS));
    }, [dispatch]);

    useEffect(() => {
        onCambios(conCambios);
    }, [conCambios, onCambios]);

    useEffect(() => {
        if (!conCambios) {
            return undefined;
        }
        const avisar = (e) => {
            e.preventDefault();
            e.returnValue = MENSAJE_SALIDA;
            return MENSAJE_SALIDA;
        };
        window.addEventListener("beforeunload", avisar);
        return () => window.removeEventListener("beforeunload", avisar);
    }, [conCambios]);

    useEffect(() => {
        const resultado = evaluacion ? _update : _create;
        if (!guardando || resultado.status === 0) {
            return;
        }
        dispatch(clear_igi(evaluacion ? UPDATE_EVALUACION_SUCCESS : CREATE_EVALUACION_SUCCESS));
        setGuardando(false);
        if (resultado.status === 200 || resultado.status === 201) {
            toast.success(evaluacion ? "La evaluación se corrigió correctamente" : "La evaluación se guardó correctamente");
            setConCambios(false);
            borrarBorrador(clave);
            onGuardado();
        } else {
            toast.error(resultado.message || "No se pudo guardar la evaluación");
        }
    }, [_create, _update, guardando, evaluacion, dispatch, onGuardado, clave]);

    if (criteriosCargando) {
        return (
            <div className="alert alert-info" role="status" aria-busy="true">
                <i className="bx bx-loader-alt bx-spin mr-50"/> Cargando criterios…
            </div>
        );
    }
    if (criteriosError) {
        return <div className="alert alert-danger">{criteriosError}</div>;
    }

    const cambiarNota = (criterioId, campo, valor) => {
        setNotas((prev) => ({
            ...prev,
            [criterioId]: {...(prev[criterioId] || {valor: "", comentario: ""}), [campo]: valor},
        }));
        setConCambios(true);
    };

    const cambiarTexto = (e) => {
        setTextos({...textos, [e.target.name]: e.target.value});
        setConCambios(true);
    };

    const copiarAnterior = () => {
        if (Object.keys(notas).length > 0 && !window.confirm("Se reemplazarán las notas escritas. ¿Continuar?")) {
            return;
        }
        setNotas(notasDesde(anterior));
        setConCambios(true);
    };

    const cancelar = () => {
        if (conCambios && !window.confirm(MENSAJE_SALIDA)) {
            return;
        }
        setConCambios(false);
        borrarBorrador(clave);
        onCancelarCorreccion();
    };

    const guardar = (e) => {
        e.preventDefault();
        const payload = {notas: [], ...textos};
        for (const c of activos) {
            const nota = notas[c.id];
            if (!nota) {
                continue;
            }
            const valor = leerNota(nota.valor);
            if (valor === null) {
                continue;
            }
            if (Number.isNaN(valor)) {
                toast.error(`La nota de "${c.nombre}" debe estar entre 1 y 5, con un decimal como máximo`);
                return;
            }
            payload.notas.push({criterio: c.id, valor: valor, comentario: (nota.comentario || "").trim()});
        }
        if (payload.notas.length === 0) {
            toast.error("Debe calificar al menos un criterio");
            return;
        }
        setGuardando(true);
        if (evaluacion) {
            dispatch(update_evaluacion(token, evaluacion.id, payload));
        } else {
            dispatch(create_evaluacion(token, tiendaId, payload));
        }
    };

    return (
        <form onSubmit={guardar}>
            {guardando && <LoadingSpinner/>}
            {evaluacion && (
                <div className="alert alert-warning d-flex justify-content-between align-items-center">
                    <span>Corrigiendo la evaluación del {formatoFecha(evaluacion.fecha)}</span>
                    <Button type="button" theme="light" classes="btn-sm" text="Cancelar corrección" onChange={cancelar}/>
                </div>
            )}
            {!evaluacion && anterior && (
                <Button
                    type="button"
                    theme="outline-primary"
                    classes="mb-1"
                    text={<><i className="bx bx-copy"/> Copiar notas de la evaluación vigente</>}
                    onChange={copiarAnterior}
                />
            )}
            {ejesAgrupados.map((eje) => (
                <div key={eje.id} className="card mb-1">
                    <div className="card-header p-1">
                        <h5 className="card-title mb-0">
                            {eje.codigo} · {eje.nombre} <small className="text-muted">(peso {eje.peso})</small>
                        </h5>
                    </div>
                    <div className="card-body p-1">
                        {eje.grupos.map((grupo) => (
                            <div key={grupo.nombre || "sin-grupo"}>
                                {grupo.nombre && <h6 className="text-muted mt-1">{grupo.nombre}</h6>}
                                {grupo.criterios.map((c) => {
                                    const nota = notas[c.id] || {valor: "", comentario: ""};
                                    const sinNota = String(nota.valor).trim() === "";
                                    return (
                                        <div key={c.id} className="border-bottom py-50">
                                            <div className="d-flex align-items-center">
                                                <label htmlFor={`nota-${c.id}`} className="flex-grow-1 mb-0 mr-50">{c.nombre}</label>
                                                <input
                                                    id={`nota-${c.id}`}
                                                    type="text"
                                                    inputMode="decimal"
                                                    className="form-control text-center"
                                                    style={{width: "5.5rem"}}
                                                    placeholder="1 a 5"
                                                    value={nota.valor}
                                                    onChange={(e) => cambiarNota(c.id, "valor", e.target.value)}
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm mt-50"
                                                placeholder={sinNota ? "Sin dato" : "Comentario (opcional)"}
                                                aria-label={`Comentario de ${c.nombre}`}
                                                value={nota.comentario}
                                                disabled={sinNota}
                                                onChange={(e) => cambiarNota(c.id, "comentario", e.target.value)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <div className="card mb-1">
                <div className="card-body p-1">
                    {TEXTOS.map(([campo, etiqueta]) => (
                        <div key={campo} className="form-group">
                            <label htmlFor={`igi-${campo}`}>{etiqueta}</label>
                            <textarea
                                id={`igi-${campo}`}
                                name={campo}
                                className="form-control"
                                rows={2}
                                value={textos[campo]}
                                onChange={cambiarTexto}
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div style={{position: "sticky", bottom: 0, background: "#fff", padding: "0.5rem 0", zIndex: 5}}>
                {conCambios && (
                    <div className="small text-muted text-center mb-50">
                        <i className="bx bx-save"/> Borrador guardado en este dispositivo — toque Guardar para enviarlo
                    </div>
                )}
                <Button
                    type="submit"
                    theme="success"
                    classes="btn-block"
                    disabled={guardando}
                    text={<><i className="bx bx-save"/> Guardar</>}
                />
            </div>
        </form>
    );
};

export default Evaluar;
